import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { FileQuestion, FolderOpen, History, Library, Mail, Plus, Settings2, Sparkles, Upload, X } from 'lucide-react';
import { MAX_UPLOAD_BYTES, type AttachmentRequest, type LibraryFile, type LibrarySuggestion } from '@mailpilot/shared';
import { api, type ResOf } from '../lib/api';
import { qk } from '../lib/keys';
import { uploadFile } from '../lib/upload';
import { bytes, displayName, fileKind, relativeTime, shortDate } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  Notice,
  Page,
  PageHeader,
  Skeleton,
  SkeletonLines,
  Switch,
  useToast,
} from '../components/ui';
import { ResolveRequestDialog } from '../components/library/ResolveRequestDialog';
import { ShareLinksDrawer } from '../components/library/ShareLinksDrawer';
import { LibraryFileCard, type LibraryPatch } from '../components/library/LibraryFileCard';
import { fileIcon } from '../components/mail/AttachmentChip';

type LibraryData = ResOf<'GET /api/library'>;
type LibrarySettings = LibraryData['settings'];

function stripExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
}

export function LibraryPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const library = useQuery({ queryKey: qk.library, queryFn: () => api('GET /api/library') });
  const pickerRef = useRef<HTMLInputElement>(null);

  const [shareFile, setShareFile] = useState<LibraryFile | null>(null);
  const [resolving, setResolving] = useState<AttachmentRequest | null>(null);

  // ----- Settings (optimistic) -----
  const saveSettings = useMutation({
    mutationFn: (patch: Partial<LibrarySettings>) => api('PATCH /api/library-settings', { body: patch }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: qk.library });
      const prev = qc.getQueryData<LibraryData>(qk.library);
      if (prev) qc.setQueryData<LibraryData>(qk.library, { ...prev, settings: { ...prev.settings, ...patch } });
      return { prev };
    },
    onError: (e, _p, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.library, ctx.prev);
      toast.error(e);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.library }),
  });

  // ----- File edits (optimistic) -----
  const patchFile = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: LibraryPatch }) => api('PATCH /api/library/:id', { params: { id }, body: patch }),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: qk.library });
      const prev = qc.getQueryData<LibraryData>(qk.library);
      if (prev) qc.setQueryData<LibraryData>(qk.library, { ...prev, files: prev.files.map((f) => (f.id === id ? { ...f, ...patch } : f)) });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.library, ctx.prev);
      toast.error(e);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.library }),
  });
  const onPatch = (id: string, patch: LibraryPatch) => patchFile.mutate({ id, patch });

  const files = useMemo(() => {
    const list = [...(library.data?.files ?? [])];
    // current versions first, then superseded; newest first within each
    return list.sort((a, b) => Number(!!a.supersededById) - Number(!!b.supersededById) || b.createdAt.localeCompare(a.createdAt));
  }, [library.data]);

  // keep the drawer's file fresh after edits
  const shareFileFresh = shareFile ? (files.find((f) => f.id === shareFile.id) ?? shareFile) : null;
  const approvedCount = files.filter((f) => f.approved && !f.supersededById).length;

  return (
    <Page>
      <PageHeader
        tone="lavender"
        icon={<Library className="h-5 w-5" />}
        title="Attachment Library"
        description="Approved, reusable files you can send as attachments or protected links. Repeated use in sent mail is only ever a suggestion — never permission."
        actions={
          <Button variant="primary" icon={<Upload className="h-4 w-4" />} onClick={() => pickerRef.current?.click()}>
            Upload file
          </Button>
        }
      />
      <div className="space-y-5">
        <RequestsCard onResolve={setResolving} />
        <UploadCard pickerRef={pickerRef} />

        <Card>
          <CardHeader
            icon={<FolderOpen className="h-5 w-5" />}
            title="Library files"
            description="Approval, permissions and expiry apply to each exact file version."
            actions={
              files.length > 0 ? (
                <span className="text-[0.8125rem] text-muted">
                  {files.length} {files.length === 1 ? 'file' : 'files'} · {approvedCount} approved
                </span>
              ) : undefined
            }
          />
          <CardBody className="space-y-3 pt-5">
            {library.isLoading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-card" />
                ))}
              </div>
            ) : library.isError ? (
              <ErrorState error={library.error} onRetry={() => void library.refetch()} />
            ) : files.length === 0 ? (
              <EmptyState
                className="py-10"
                icon={<FolderOpen className="h-6 w-6" />}
                title="Your library is empty"
                description="Upload a file, or add one of the suggestions from your sent history below."
                actions={
                  <Button variant="primary" icon={<Upload className="h-4 w-4" />} onClick={() => pickerRef.current?.click()}>
                    Upload file
                  </Button>
                }
              />
            ) : (
              files.map((f) => <LibraryFileCard key={f.id} file={f} onPatch={onPatch} onShare={setShareFile} />)
            )}
          </CardBody>
        </Card>

        <SuggestionsCard suggestEnabled={library.data?.settings.suggestReusable ?? true} />
        <SettingsCard library={library} onChange={(p) => saveSettings.mutate(p)} />
      </div>

      <ShareLinksDrawer file={shareFileFresh} open={!!shareFile} onClose={() => setShareFile(null)} />
      <ResolveRequestDialog request={resolving} open={!!resolving} onClose={() => setResolving(null)} />
    </Page>
  );
}

// ---------- Settings ----------

function SettingsCard({ library, onChange }: { library: UseQueryResult<LibraryData>; onChange: (p: Partial<LibrarySettings>) => void }) {
  const s = library.data?.settings;
  return (
    <Card>
      <CardHeader icon={<Settings2 className="h-5 w-5" />} title="Library behaviour" description="Three separate switches — each one is independent." />
      <CardBody className="pt-5">
        {library.isLoading ? (
          <SkeletonLines lines={6} />
        ) : library.isError || !s ? (
          <ErrorState compact error={library.error} onRetry={() => void library.refetch()} />
        ) : (
          <div className="divide-y divide-line/60">
            <div className="pb-4">
              <Switch
                checked={s.suggestReusable}
                onChange={(v) => onChange({ suggestReusable: v })}
                label="Suggest reusable attachments"
                description="Recognise files you've sent repeatedly and suggest adding them to the library. Suggestions never grant permission to send."
              />
            </div>
            <div className="py-4">
              <Switch
                checked={s.sendAsLinks}
                onChange={(v) => onChange({ sendAsLinks: v })}
                label="Send approved files as links"
                description="Share approved files as protected links that expire, can be revoked and record each access, instead of attaching them. You can still choose a traditional attachment each time."
              />
            </div>
            <div className="pt-4">
              <Switch
                checked={s.autoSendLinked}
                onChange={(v) => onChange({ autoSendLinked: v })}
                label="Automatically send an approved linked attachment under a matching rule"
                description="Only happens when BOTH the responder rule and this file's permission allow that exact file/version and recipient. Otherwise MailPilot asks you to confirm first."
              />
              {s.autoSendLinked && !s.sendAsLinks && (
                <Notice tone="warn" className="mt-3">
                  Automatic sending delivers files as protected links, which are currently off. Turn on “Send approved files as links” as well.
                </Notice>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ---------- Open attachment requests ----------

function RequestsCard({ onResolve }: { onResolve: (r: AttachmentRequest) => void }) {
  const q = useQuery({ queryKey: qk.attachmentRequests('open'), queryFn: () => api('GET /api/attachment-requests', { query: { status: 'open' } }) });
  const requests = q.data?.requests ?? [];
  if (q.isSuccess && requests.length === 0) return null;
  return (
    <Card className="border-warn/30">
      <CardHeader
        icon={<FileQuestion className="h-5 w-5" />}
        title={
          <span className="inline-flex items-center gap-2">
            Open attachment requests {requests.length > 0 && <Badge tone="warn">{requests.length}</Badge>}
          </span>
        }
        description="These replies are waiting for a file. Choose or upload the right document to continue."
      />
      <CardBody className="pt-4">
        {q.isLoading ? (
          <SkeletonLines lines={3} />
        ) : q.isError ? (
          <ErrorState compact error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <ul className="-mx-3 space-y-0.5">
            {requests.map((r) => (
              <li key={r.id} className="flex flex-col gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-raised sm:flex-row sm:items-center">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-warn-soft text-warn-ink">
                  <FileQuestion className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink">{r.description}</p>
                  {r.reason && <p className="mt-0.5 text-[0.8125rem] text-muted">{r.reason}</p>}
                  <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                    <Mail className="h-3.5 w-3.5" aria-hidden />
                    {r.requestedBy ? displayName(r.requestedBy) : 'Unknown sender'} · {relativeTime(r.createdAt)}
                    {r.threadId && (
                      <Link to={`/inbox/${r.threadId}`} className="font-semibold text-primary-ink hover:underline">
                        Open email
                      </Link>
                    )}
                  </p>
                </div>
                <Button variant="primary" onClick={() => onResolve(r)}>
                  Resolve
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// ---------- Upload ----------

function UploadCard({ pickerRef }: { pickerRef: React.RefObject<HTMLInputElement> }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');

  const add = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file first');
      const up = await uploadFile(file);
      return api('POST /api/library', { body: { uploadId: up.uploadId, friendlyName: name.trim() || stripExt(file.name) } });
    },
    onSuccess: (r) => {
      toast.success(`“${r.file.friendlyName}” added. Approve it when you're ready to use it in replies.`);
      setFile(null);
      setName('');
      if (pickerRef.current) pickerRef.current.value = '';
      void qc.invalidateQueries({ queryKey: qk.library });
    },
    onError: (e) => toast.error(e),
  });

  const tooBig = !!file && file.size > MAX_UPLOAD_BYTES;
  const clear = () => {
    setFile(null);
    setName('');
    if (pickerRef.current) pickerRef.current.value = '';
  };

  return (
    <>
      <input
        ref={pickerRef}
        type="file"
        className="sr-only"
        aria-label="Choose a file to add to the library"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setFile(f);
          if (f && !name.trim()) setName(stripExt(f.name));
        }}
      />
      {file && (
        <Card>
          <CardHeader icon={<Upload className="h-5 w-5" />} title="Add to library" description={`Maximum ${bytes(MAX_UPLOAD_BYTES)}. Uploads are scanned before they can be shared, and start unapproved.`} actions={<IconButton label="Cancel upload" icon={<X className="h-[18px] w-[18px]" />} onClick={clear} />} />
          <CardBody className="pt-4">
            <form
              className="grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end"
              onSubmit={(e) => {
                e.preventDefault();
                if (file && !tooBig) add.mutate();
              }}
            >
              <Field label="File" error={tooBig ? `This file is ${bytes(file.size)} — the limit is ${bytes(MAX_UPLOAD_BYTES)}.` : undefined}>
                {(id) => (
                  <div id={id} className="flex h-10 items-center gap-2.5 rounded-xl border border-line bg-raised/60 px-3">
                    <span className="text-primary-ink">{fileIcon(file.type, 'h-4 w-4')}</span>
                    <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-ink">{file.name}</span>
                    <span className="shrink-0 text-xs text-muted">
                      {fileKind(file.type, file.name)} · {bytes(file.size)}
                    </span>
                    <button type="button" onClick={() => pickerRef.current?.click()} className="shrink-0 text-xs font-semibold text-primary-ink hover:underline">
                      Change
                    </button>
                  </div>
                )}
              </Field>
              <Field label="Friendly name" hint="Shown to you when choosing files.">
                {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Price list 2026" />}
              </Field>
              <Button type="submit" variant="primary" icon={<Plus className="h-4 w-4" />} loading={add.isPending} disabled={!file || tooBig} className="md:mb-[1.375rem]">
                Upload
              </Button>
            </form>
          </CardBody>
        </Card>
      )}
    </>
  );
}

// ---------- Suggestions from sent history ----------

function SuggestionsCard({ suggestEnabled }: { suggestEnabled: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: qk.librarySuggestions, queryFn: () => api('GET /api/library/suggestions') });

  const addApprove = useMutation({
    mutationFn: async (s: LibrarySuggestion) => {
      const r = await api('POST /api/library', { body: { attachmentId: s.sampleAttachmentId, friendlyName: stripExt(s.filename) } });
      return api('PATCH /api/library/:id', { params: { id: r.file.id }, body: { approved: true } });
    },
    onSuccess: (r) => {
      toast.success(`“${r.file.friendlyName}” added and approved. Set allowed recipients before enabling auto-send.`);
      void qc.invalidateQueries({ queryKey: qk.library });
      void qc.invalidateQueries({ queryKey: qk.librarySuggestions });
    },
    onError: (e) => {
      toast.error(e);
      void qc.invalidateQueries({ queryKey: qk.library });
    },
  });

  const suggestions = q.data?.suggestions ?? [];

  return (
    <Card>
      <CardHeader icon={<Sparkles className="h-5 w-5" />} title="Suggestions from sent history" description="Files you've sent repeatedly. A suggestion is not permission — nothing is used until you approve it." />
      <CardBody className="pt-4">
        {!suggestEnabled && (
          <Notice tone="info" className="mb-3">
            “Suggest reusable attachments” is off, so new suggestions aren't being collected.
          </Notice>
        )}
        {q.isLoading ? (
          <SkeletonLines lines={4} />
        ) : q.isError ? (
          <ErrorState compact error={q.error} onRetry={() => void q.refetch()} />
        ) : suggestions.length === 0 ? (
          <EmptyState icon={<History className="h-6 w-6" />} title="No suggestions yet" description="As your sent history is imported, files you send often will appear here." className="py-6" />
        ) : (
          <ul className="-mx-3 space-y-0.5">
            {suggestions.map((s) => (
              <li key={s.contentHash} className="group flex flex-col gap-2 rounded-xl px-3 py-2.5 transition-colors hover:bg-raised sm:flex-row sm:items-center sm:gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-raised text-muted">
                  <History className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-ink">{s.filename}</p>
                  <p className="text-xs text-muted">
                    Sent {s.timesSent} {s.timesSent === 1 ? 'time' : 'times'} · last sent {shortDate(s.lastSentAt)} · suggestion only, not permission
                  </p>
                  {s.recipients.length > 0 && (
                    <p className="truncate text-xs text-muted" title={s.recipients.join(', ')}>
                      To: {s.recipients.slice(0, 4).join(', ')}
                      {s.recipients.length > 4 ? ` +${s.recipients.length - 4} more` : ''}
                    </p>
                  )}
                </div>
                {s.alreadyInLibrary ? (
                  <Badge tone="success">In library</Badge>
                ) : (
                  <Button
                    variant="soft"
                    size="sm"
                    icon={<Plus className="h-4 w-4" />}
                    loading={addApprove.isPending && addApprove.variables?.contentHash === s.contentHash}
                    disabled={addApprove.isPending}
                    onClick={() => addApprove.mutate(s)}
                  >
                    Add &amp; approve
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
